// IMPORTACIONES
import { Helmet } from 'react-helmet-async' // Para SEO (meta tags, title, etc) - NO depende de otro archivo
import { useParams, Link, useNavigate } from 'react-router-dom' // Hooks de React Router - NO depende de otro archivo
import { useState, useEffect } from 'react' // Hooks de React - NO depende de otro archivo
import { supabase, db } from '../lib/supabase' // Funciones de base de datos - DEPENDE de: src/lib/supabase.js
import { useRecaptcha } from '../hooks/useRecaptcha' // Hook de reCAPTCHA
import LoadingSpinner from '../components/LoadingSpinner' // Componente de carga - DEPENDE de: src/components/LoadingSpinner.jsx

// COMPONENTE PRINCIPAL
export default function TiendaDetalle() {
  // ESTADOS Y HOOKS
  const { id } = useParams() // Obtiene el ID de la tienda desde la URL (ej: /tiendas2/123)
  const navigate = useNavigate() // Para navegar a otras páginas
  const { verifyRecaptcha } = useRecaptcha() // Hook de reCAPTCHA
  const [tienda, setTienda] = useState(null) // Almacena los datos de la tienda desde la BD
  const [loading, setLoading] = useState(true) // Estado de carga inicial
  const [error, setError] = useState(null) // Almacena mensajes de error
  const [activeTab, setActiveTab] = useState('info') // Pestaña activa: 'info', 'descuentos', 'comentarios', o 'galeria'
  const [comentarios, setComentarios] = useState([]) // Lista de comentarios publicados de la tienda
  const [faqs, setFaqs] = useState([]) // Lista de preguntas frecuentes de la tienda
  const [nuevoComentario, setNuevoComentario] = useState({ // Formulario de nuevo comentario
    nombre: '', // Nombre del usuario
    email: '', // Email del usuario
    comentario: '', // Texto del comentario
    rating: 5 // Calificación de 1 a 5 estrellas
  })
  const [submittingComentario, setSubmittingComentario] = useState(false) // Estado de envío del formulario

  // EFECTO: Se ejecuta cuando cambia el ID de la URL
  useEffect(() => {
    fetchTienda() // Carga los datos de la tienda
    fetchComentarios() // Carga los comentarios
    fetchFAQs() // Carga las FAQs
  }, [id]) // Se ejecuta cada vez que cambia el ID en la URL

  // FUNCIÓN: Obtiene los datos de la tienda desde la base de datos
  // DEPENDE de: src/lib/supabase.js (función db.getTiendaById)
  const fetchTienda = async () => {
    try {
      setLoading(true) // Activa el estado de carga
      const { data, error } = await db.getTiendaById(id) // Consulta a Supabase tabla 'tiendas'
      
      if (error) throw error // Si hay error, lanza excepción
      if (!data) { // Si no se encontró la tienda
        setError('Tienda no encontrada') // Muestra mensaje de error
        return
      }
      
      setTienda(data) // Guarda los datos de la tienda en el estado
    } catch (error) {
      console.error('Error:', error)
      setError('Error al cargar la tienda') // Muestra mensaje de error genérico
    } finally {
      setLoading(false) // Desactiva el estado de carga siempre
    }
  }

  // FUNCIÓN: Obtiene los comentarios publicados de la tienda
  // DEPENDE de: src/lib/supabase.js (función db.getComentariosPublicados)
  const fetchComentarios = async () => {
    try {
      const { data } = await db.getComentariosPublicados(id) // Consulta a Supabase tabla 'comentarios' donde publicado=true
      setComentarios(data || []) // Guarda los comentarios (o array vacío si no hay)
    } catch (error) {
      console.error('Error fetching comentarios:', error)
    }
  }

  // FUNCIÓN: Obtiene las preguntas frecuentes de esta tienda específica
  // DEPENDE de: src/lib/supabase.js (función db.getFAQsByScope)
  const fetchFAQs = async () => {
    try {
      const { data } = await db.getFAQsByScope('tienda', id) // Consulta a Supabase tabla 'faqs' donde scope='tienda' y entity_id=id
      setFaqs(data || []) // Guarda las FAQs (o array vacío si no hay)
    } catch (error) {
      console.error('Error fetching FAQs:', error)
    }
  }

  // FUNCIÓN: Maneja el envío del formulario de comentarios
  // DEPENDE de: src/lib/supabase.js (función db.createComentario)
  const handleSubmitComentario = async (e) => {
    e.preventDefault() // Previene que el formulario recargue la página
    
    // Validación: Verifica que todos los campos estén completos
    if (!nuevoComentario.nombre || !nuevoComentario.email || !nuevoComentario.comentario) {
      alert('Por favor completa todos los campos')
      return
    }

    try {
      setSubmittingComentario(true) // Desactiva el botón de envío

      // Verify reCAPTCHA
      const recaptchaResult = await verifyRecaptcha('comentario_tienda')
      if (!recaptchaResult.success) {
        alert('❌ Error de verificación. Por favor intenta de nuevo.')
        setSubmittingComentario(false)
        return
      }

      const { error } = await db.createComentario({ // Inserta en tabla 'comentarios' de Supabase
        tienda_id: id, // ID de la tienda actual
        nombre: nuevoComentario.nombre,
        email: nuevoComentario.email,
        comentario: nuevoComentario.comentario,
        rating: nuevoComentario.rating,
        publicado: false // Los comentarios requieren aprobación del admin
      })

      if (error) throw error

      alert('✅ Comentario enviado. Será publicado después de revisión.') // Mensaje de éxito
      setNuevoComentario({ nombre: '', email: '', comentario: '', rating: 5 }) // Limpia el formulario
    } catch (error) {
      console.error('Error:', error)
      alert('❌ Error al enviar el comentario')
    } finally {
      setSubmittingComentario(false) // Reactiva el botón de envío
    }
  }

  // FUNCIÓN: Calcula el promedio de calificaciones de los comentarios
  const calculateAverageRating = () => {
    if (comentarios.length === 0) return 0 // Si no hay comentarios, retorna 0
    const sum = comentarios.reduce((acc, c) => acc + c.rating, 0) // Suma todas las calificaciones
    return (sum / comentarios.length).toFixed(1) // Retorna el promedio con 1 decimal (ej: 4.5)
  }

  // FUNCIÓN: Convierte un número de rating en estrellas (ej: 4 → ⭐⭐⭐⭐)
  const getRatingStars = (rating) => {
    return '⭐'.repeat(Math.round(rating)) // Repite el emoji de estrella según el rating redondeado
  }

  // RENDERIZADO CONDICIONAL: Muestra diferentes estados
  if (loading) return <LoadingSpinner /> // Mientras carga, muestra el spinner
  if (error) return ( // Si hay error, muestra mensaje y link para volver
    <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
      <h2>{error}</h2>
      <Link to="/tiendas2" style={{ color: 'var(--color-cta-bg)' }}>
        Volver a Tiendas
      </Link>
    </div>
  )
  if (!tienda) return null // Si no hay tienda, no renderiza nada

  // CÁLCULO: Obtiene el rating promedio para usar en el render
  const avgRating = calculateAverageRating()

  return (
    <>
      <Helmet>
        <title>{tienda.seo_title || `${tienda.nombre} - Plaza Aurora`}</title>
        <meta name="description" content={tienda.seo_description || tienda.descripcion || `Visita ${tienda.nombre} en Plaza Aurora Outlet.`} />
        <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/tiendas2/${tienda.id}`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="business.business" />
        <meta property="og:locale" content="es_PE" />
        <meta property="og:site_name" content="Plaza Aurora" />
        <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/tiendas2/${tienda.id}`} />
        <meta property="og:title" content={tienda.seo_title || `${tienda.nombre} - Plaza Aurora`} />
        <meta property="og:description" content={tienda.seo_description || tienda.descripcion || `Visita ${tienda.nombre} en Plaza Aurora Outlet.`} />
        {tienda.logo_url && <meta property="og:image" content={tienda.logo_url} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={tienda.seo_title || `${tienda.nombre} - Plaza Aurora`} />
        <meta name="twitter:description" content={tienda.seo_description || tienda.descripcion || `Visita ${tienda.nombre} en Plaza Aurora Outlet.`} />
        {tienda.logo_url && <meta name="twitter:image" content={tienda.logo_url} />}
        
        {/* Robots */}
        <meta name="robots" content={tienda.robots_index === false ? 'noindex,nofollow' : 'index,follow'} />
      </Helmet>

      {/* Breadcrumb JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/`
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Tiendas",
              "item": `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/tiendas2`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": tienda.nombre,
              "item": `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/tiendas2/${tienda.id}`
            }
          ]
        })}
      </script>

      {/* Store Schema.org */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Store",
          "name": tienda.nombre,
          "description": tienda.descripcion,
          "image": tienda.logo_url,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Av. Carlos Izaguirre 271-275",
            "addressLocality": "Independencia",
            "addressRegion": "Lima",
            "addressCountry": "PE"
          },
          ...(comentarios.length > 0 && {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": avgRating,
              "reviewCount": comentarios.length
            }
          })
        })}
      </script>

      {tienda.json_ld && (
        <script type="application/ld+json">
          {tienda.json_ld}
        </script>
      )}

      {/* CONTENEDOR PRINCIPAL */}
      <main>
        {/* IMPORTANTE: Este div es el contenedor principal que centra todo el contenido */}
        <div style={{
          maxWidth: '1200px', // Ancho máximo del contenido (en desktop se centra)
          margin: '0 16px', // Centra el contenedor horizontalmente
          padding: '0 0' // PADDING FIJO: 16px a cada lado - ESTE ES EL ESPACIO HORIZONTAL EN MOBILE
        }}>
          {/* BREADCRUMB (Inicio / Tiendas / Nombre) */}
          {/* NO depende de otro archivo */}
          <nav style={{ 
            marginBottom: '1.5rem', // Espacio debajo del breadcrumb
            marginTop: '2rem' // Espacio arriba del breadcrumb (separación del header)
          }}>
            {/* Contenedor flex del breadcrumb */}
            <div style={{
              display: 'flex', // Elementos en línea
              gap: '0.5rem', // Espacio entre elementos
              fontSize: 'var(--font-size-small)', // Tamaño de fuente pequeño - DEPENDE de: src/index.css (variables CSS)
              color: 'var(--color-text-muted)', // Color de texto gris - DEPENDE de: src/index.css (variables CSS)
              flexWrap: 'wrap' // Si no caben en una línea, se envuelven
            }}>
              <Link to="/" style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>Inicio</Link>
              <span>/</span>
              <Link to="/tiendas2" style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>Tiendas</Link>
              <span>/</span>
              <span style={{ color: 'var(--color-text-primary)', fontWeight: 'var(--font-weight-bold)' }}>{tienda.nombre}</span>
            </div>
          </nav>

          {/* GRID PRINCIPAL: Divide la página en 2 columnas (izquierda: info, derecha: sidebar) */}
          {/* IMPORTANTE: Este grid contiene TODO el contenido de la página */}
          <div style={{
  display: 'grid',
  gridTemplateColumns: window.innerWidth >= 1024 ? '2fr 1fr' : '1fr',
  gap: window.innerWidth >= 768 ? '2rem' : '1rem',
  maxWidth: '100%',
  width: '100%',
  boxSizing: 'border-box'
}}>
            {/* COLUMNA IZQUIERDA: Información principal de la tienda */}
            <div style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
              {/* HEADER DE LA TIENDA: Logo + Nombre + Categoría + Rating */}
              {/* IMPORTANTE: Este div contiene el logo y el título */}
              <div style={{
                display: 'flex', // Logo a la izquierda, info a la derecha
                alignItems: 'flex-start', // Alinea arriba
                gap: '1rem', // Espacio entre logo e info
                marginBottom: '1.5rem', // Espacio debajo
                flexWrap: 'wrap' // En pantallas pequeñas, el contenido se envuelve
              }}>
                {/* LOGO DE LA TIENDA */}
                {tienda.logo_url && ( // Solo se muestra si la tienda tiene logo
                  <div style={{
                    width: window.innerWidth >= 768 ? '120px' : '80px', // Desktop: 120px, Mobile: 80px
                    height: window.innerWidth >= 768 ? '120px' : '80px', // Cuadrado
                    background: 'var(--color-accent-gray)', // Fondo gris - DEPENDE de: src/index.css
                    borderRadius: 'var(--radius-lg)', // Bordes redondeados - DEPENDE de: src/index.css
                    padding: window.innerWidth >= 768 ? '1rem' : '0.5rem', // Padding interno del logo
                    flexShrink: 0 // No se encoge cuando el espacio es reducido
                  }}>
                    {/* Imagen del logo */}
                    <img
                      src={tienda.logo_url} // URL del logo desde la base de datos
                      alt={tienda.nombre} // Texto alternativo para accesibilidad
                      style={{
                        width: '100%', // Ocupa todo el ancho del contenedor
                        height: '100%', // Ocupa toda la altura del contenedor
                        objectFit: 'contain' // Mantiene proporciones, no se corta
                      }}
                    />
                  </div>
                )}
                {/* INFORMACIÓN DE LA TIENDA: Nombre, Categoría, Rating */}
                <div style={{ 
                  flex: 1, // Ocupa todo el espacio disponible
                  minWidth: 0 // IMPORTANTE: Permite que el texto se rompa en lugar de causar overflow
                }}>
                  {/* NOMBRE DE LA TIENDA */}
                  <h1 style={{
                    fontFamily: 'var(--font-display)', // Fuente para títulos - DEPENDE de: src/index.css
                    fontSize: window.innerWidth >= 768 ? 'var(--font-size-h1)' : 'var(--font-size-h2)', // Desktop: h1, Mobile: h2 - DEPENDE de: src/index.css
                    fontWeight: 'var(--font-weight-bold)', // Peso de fuente bold - DEPENDE de: src/index.css
                    color: 'var(--color-text-primary)', // Color de texto principal - DEPENDE de: src/index.css
                    margin: '0 0 0.5rem 0', // Sin margen arriba/lados, 0.5rem abajo
                    wordBreak: 'break-word' // IMPORTANTE: Rompe palabras largas para evitar overflow
                  }}>
                    {tienda.nombre}
                  </h1>
                  {/* BADGE DE CATEGORÍA (ej: "Banco/Cajero Automático") */}
                  {tienda.categoria && ( // Solo se muestra si la tienda tiene categoría
                    <div style={{
                      display: 'inline-block', // Badge en línea
                      padding: '0.25rem 0.75rem', // Padding interno del badge
                      background: 'var(--color-accent-gray)', // Fondo gris - DEPENDE de: src/index.css
                      borderRadius: 'var(--radius-sm)', // Bordes redondeados pequeños - DEPENDE de: src/index.css
                      fontSize: 'var(--font-size-small)', // Tamaño de fuente pequeño - DEPENDE de: src/index.css
                      fontWeight: 'var(--font-weight-bold)', // Negrita - DEPENDE de: src/index.css
                      color: 'var(--color-text-primary)', // Color de texto - DEPENDE de: src/index.css
                      marginBottom: '0.75rem' // Espacio debajo
                    }}>
                      {tienda.categoria.nombre}
                    </div>
                  )}
                  {/* RATING (Estrellas + Promedio + Número de reseñas) */}
                  {comentarios.length > 0 && ( // Solo se muestra si hay comentarios
                    <div style={{
                      display: 'flex', // Estrellas y texto en línea
                      alignItems: 'center', // Centrado vertical
                      gap: '0.5rem', // Espacio entre estrellas y texto
                      fontSize: 'var(--font-size-body)' // Tamaño de fuente normal - DEPENDE de: src/index.css
                    }}>
                      <span>{getRatingStars(avgRating)}</span> {/* Muestra las estrellas (ej: ⭐⭐⭐⭐) */}
                      <span style={{ color: 'var(--color-text-muted)' }}> {/* Color gris para el texto - DEPENDE de: src/index.css */}
                        {avgRating} ({comentarios.length} reseña{comentarios.length !== 1 ? 's' : ''})
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* SECCIÓN: ACERCA DE (Descripción de la tienda) */}
              {tienda.descripcion && ( // Solo se muestra si hay descripción
                <div style={{
                  background: 'var(--color-accent-gray)', // Fondo gris claro - DEPENDE de: src/index.css
                  padding: window.innerWidth >= 768 ? '1.5rem' : '1rem', // Padding reducido en mobile
                  borderRadius: 'var(--radius-lg)', // Bordes redondeados - DEPENDE de: src/index.css
                  marginBottom: '1.5rem', // Espacio debajo
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                }}>
                  <h2 style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-h3)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--color-text-primary)',
                    margin: '0 0 0.75rem 0'
                  }}>
                    Acerca de
                  </h2>
                  <p style={{
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--color-text-muted)',
                    lineHeight: 'var(--line-height-relaxed)',
                    margin: 0
                  }}>
                    {tienda.descripcion}
                  </p>
                </div>
              )}

              {/* SECCIÓN: TABS (Información, Descuentos, Reseñas) */}
              {/* IMPORTANTE: Esta sección contiene las pestañas y su contenido */}
              <div style={{ 
                marginTop: '2rem' // Espacio arriba
              }}>
                {/* BARRA DE TABS (Botones de las pestañas) */}
                {/* IMPORTANTE: Este div puede causar overflow horizontal si no se maneja bien */}
                <div style={{
                  display: 'flex', // Tabs en línea horizontal
                  gap: window.innerWidth >= 768 ? '1rem' : '0.5rem', // Espacio entre tabs (desktop: 1rem, mobile: 0.5rem)
                  borderBottom: '2px solid var(--color-stroke)', // Línea debajo de los tabs - DEPENDE de: src/index.css
                  marginBottom: '1.5rem', // Espacio debajo de la barra de tabs
                  overflowX: 'auto', // IMPORTANTE: Permite scroll horizontal si los tabs no caben
                  WebkitOverflowScrolling: 'touch' // Scroll suave en iOS
                }}>
                  <button
                    onClick={() => setActiveTab('info')}
                    style={{
                      padding: window.innerWidth >= 768 ? '0.75rem 1.5rem' : '0.5rem 1rem',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: activeTab === 'info' ? '3px solid var(--color-cta-bg)' : 'none',
                      fontFamily: 'var(--font-ui)',
                      fontSize: window.innerWidth >= 768 ? 'var(--font-size-body)' : 'var(--font-size-small)',
                      fontWeight: 'var(--font-weight-bold)',
                      color: activeTab === 'info' ? 'var(--color-cta-bg)' : 'var(--color-text-muted)',
                      cursor: 'pointer',
                      marginBottom: '-2px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Información
                  </button>
                  {tienda.descuentos && tienda.descuentos.length > 0 && (
                    <button
                      onClick={() => setActiveTab('descuentos')}
                      style={{
                        padding: window.innerWidth >= 768 ? '0.75rem 1.5rem' : '0.5rem 1rem',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: activeTab === 'descuentos' ? '3px solid var(--color-cta-bg)' : 'none',
                        fontFamily: 'var(--font-ui)',
                        fontSize: window.innerWidth >= 768 ? 'var(--font-size-body)' : 'var(--font-size-small)',
                        fontWeight: 'var(--font-weight-bold)',
                        color: activeTab === 'descuentos' ? 'var(--color-cta-bg)' : 'var(--color-text-muted)',
                        cursor: 'pointer',
                        marginBottom: '-2px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Descuentos ({tienda.descuentos.length})
                    </button>
                  )}
                  <button
                    onClick={() => setActiveTab('comentarios')}
                    style={{
                      padding: window.innerWidth >= 768 ? '0.75rem 1.5rem' : '0.5rem 1rem',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: activeTab === 'comentarios' ? '3px solid var(--color-cta-bg)' : 'none',
                      fontFamily: 'var(--font-ui)',
                      fontSize: window.innerWidth >= 768 ? 'var(--font-size-body)' : 'var(--font-size-small)',
                      fontWeight: 'var(--font-weight-bold)',
                      color: activeTab === 'comentarios' ? 'var(--color-cta-bg)' : 'var(--color-text-muted)',
                      cursor: 'pointer',
                      marginBottom: '-2px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Reseñas ({comentarios.length})
                  </button>
                  {tienda.fotos && tienda.fotos.length > 0 && (
                    <button
                      onClick={() => setActiveTab('galeria')}
                      style={{
                        padding: window.innerWidth >= 768 ? '0.75rem 1.5rem' : '0.5rem 1rem',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: activeTab === 'galeria' ? '3px solid var(--color-cta-bg)' : 'none',
                        fontFamily: 'var(--font-ui)',
                        fontSize: window.innerWidth >= 768 ? 'var(--font-size-body)' : 'var(--font-size-small)',
                        fontWeight: 'var(--font-weight-bold)',
                        color: activeTab === 'galeria' ? 'var(--color-cta-bg)' : 'var(--color-text-muted)',
                        cursor: 'pointer',
                        marginBottom: '-2px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Galería ({tienda.fotos.filter(f => f.activo).length})
                    </button>
                  )}
                </div>

                {/* Tab Content */}
                {activeTab === 'info' && (
                  <div style={{ width: '100%' }}>
                    <div style={{
                      display: 'grid',
                      gap: '1rem',
                      width: '100%'
                    }}>
                      {tienda.ubicacion && (
                        <div style={{ wordBreak: 'break-word' }}>
                          <strong style={{ color: 'var(--color-text-primary)' }}>📍 Ubicación:</strong>
                          <p style={{ margin: '0.25rem 0 0 0', color: 'var(--color-text-muted)', wordBreak: 'break-word' }}>{tienda.ubicacion}</p>
                        </div>
                      )}
                      {tienda.horario && (
                        <div>
                          <strong style={{ color: 'var(--color-text-primary)' }}>🕐 Horario:</strong>
                          <p style={{ margin: '0.25rem 0 0 0', color: 'var(--color-text-muted)' }}>{tienda.horario}</p>
                        </div>
                      )}
                      {tienda.telefono && (
                        <div>
                          <strong style={{ color: 'var(--color-text-primary)' }}>📞 Teléfono:</strong>
                          <p style={{ margin: '0.25rem 0 0 0', color: 'var(--color-text-muted)' }}>{tienda.telefono}</p>
                        </div>
                      )}
                      {tienda.email && (
                        <div style={{ wordBreak: 'break-word' }}>
                          <strong style={{ color: 'var(--color-text-primary)' }}>✉️ Email:</strong>
                          <p style={{ margin: '0.25rem 0 0 0', color: 'var(--color-text-muted)', wordBreak: 'break-all' }}>{tienda.email}</p>
                        </div>
                      )}
                      {tienda.website && (
                        <div style={{ wordBreak: 'break-word' }}>
                          <strong style={{ color: 'var(--color-text-primary)' }}>🌐 Sitio web:</strong>
                          <p style={{ margin: '0.25rem 0 0 0' }}>
                            <a href={tienda.website} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-cta-bg)', wordBreak: 'break-all' }}>
                              {tienda.website}
                            </a>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'descuentos' && tienda.descuentos && (
                  <div style={{
                    display: 'grid',
                    gap: '1rem',
                    width: '100%'
                  }}>
                    {tienda.descuentos.map(descuento => (
                      <Link
                        key={descuento.id}
                        to={`/descuentos/${descuento.id}`}
                        style={{
                          display: 'block',
                          background: 'var(--color-accent-gray)',
                          padding: window.innerWidth >= 768 ? '1.5rem' : '1rem', // Padding reducido en mobile
                          borderRadius: 'var(--radius-lg)',
                          border: '1px solid var(--color-stroke)',
                          textDecoration: 'none',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)'
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      >
                        <h3 style={{
                          fontFamily: 'var(--font-ui)',
                          fontSize: 'var(--font-size-body)',
                          fontWeight: 'var(--font-weight-bold)',
                          color: 'var(--color-text-primary)',
                          margin: '0 0 0.5rem 0'
                        }}>
                          {descuento.titulo}
                        </h3>
                        <p style={{
                          fontSize: 'var(--font-size-body)',
                          color: 'var(--color-text-muted)',
                          margin: 0
                        }}>
                          {descuento.descripcion}
                        </p>
                      </Link>
                    ))}
                  </div>
                )}

                {activeTab === 'comentarios' && (
                  <div>
                    {/* Existing Comments */}
                    {comentarios.length > 0 && (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                        marginBottom: '2rem'
                      }}>
                        {comentarios.map(comentario => (
                          <div
                            key={comentario.id}
                            style={{
                              background: 'var(--color-accent-gray)',
                              padding: window.innerWidth >= 768 ? '1.5rem' : '1rem', // Padding reducido en mobile
                              borderRadius: 'var(--radius-lg)',
                              border: '1px solid var(--color-stroke)'
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'start',
                              marginBottom: '0.75rem',
                              flexWrap: 'wrap',
                              gap: '0.5rem'
                            }}>
                              <div>
                                <strong style={{ color: 'var(--color-text-primary)' }}>{comentario.nombre}</strong>
                                <div style={{ fontSize: 'var(--font-size-small)', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
                                  {new Date(comentario.created_at).toLocaleDateString('es-PE')}
                                </div>
                              </div>
                              <div style={{ fontSize: 'var(--font-size-body)' }}>
                                {getRatingStars(comentario.rating)}
                              </div>
                            </div>
                            <p style={{
                              fontSize: 'var(--font-size-body)',
                              color: 'var(--color-text-muted)',
                              lineHeight: 'var(--line-height-relaxed)',
                              margin: 0
                            }}>
                              {comentario.comentario}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* New Comment Form */}
                    <div style={{
                      background: 'var(--color-surface)',
                      padding: window.innerWidth >= 768 ? '1.5rem' : '1rem', // Padding reducido en mobile
                      borderRadius: 'var(--radius-lg)',
                      border: '1px solid var(--color-stroke)'
                    }}>
                      <h3 style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-h3)',
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--color-text-primary)',
                        margin: '0 0 1rem 0'
                      }}>
                        Deja tu reseña
                      </h3>
                      <form onSubmit={handleSubmitComentario}>
                        <div style={{ display: 'grid', gap: '1rem' }}>
                          <div>
                            <label style={{
                              display: 'block',
                              fontSize: 'var(--font-size-body)',
                              fontWeight: 'var(--font-weight-bold)',
                              color: 'var(--color-text-primary)',
                              marginBottom: '0.5rem'
                            }}>
                              Nombre *
                            </label>
                            <input
                              type="text"
                              value={nuevoComentario.nombre}
                              onChange={(e) => setNuevoComentario({ ...nuevoComentario, nombre: e.target.value })}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--color-stroke)',
                                borderRadius: 'var(--radius-md)',
                                fontSize: 'var(--font-size-body)',
                                fontFamily: 'var(--font-ui)',
                                boxSizing: 'border-box'
                              }}
                              required
                            />
                          </div>
                          <div>
                            <label style={{
                              display: 'block',
                              fontSize: 'var(--font-size-body)',
                              fontWeight: 'var(--font-weight-bold)',
                              color: 'var(--color-text-primary)',
                              marginBottom: '0.5rem'
                            }}>
                              Email *
                            </label>
                            <input
                              type="email"
                              value={nuevoComentario.email}
                              onChange={(e) => setNuevoComentario({ ...nuevoComentario, email: e.target.value })}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--color-stroke)',
                                borderRadius: 'var(--radius-md)',
                                fontSize: 'var(--font-size-body)',
                                fontFamily: 'var(--font-ui)',
                                boxSizing: 'border-box'
                              }}
                              required
                            />
                          </div>
                          <div>
                            <label style={{
                              display: 'block',
                              fontSize: 'var(--font-size-body)',
                              fontWeight: 'var(--font-weight-bold)',
                              color: 'var(--color-text-primary)',
                              marginBottom: '0.5rem'
                            }}>
                              Calificación *
                            </label>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              {[1, 2, 3, 4, 5].map(star => (
                                <button
                                  key={star}
                                  type="button"
                                  onClick={() => setNuevoComentario({ ...nuevoComentario, rating: star })}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    opacity: star <= nuevoComentario.rating ? 1 : 0.3
                                  }}
                                >
                                  ⭐
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label style={{
                              display: 'block',
                              fontSize: 'var(--font-size-body)',
                              fontWeight: 'var(--font-weight-bold)',
                              color: 'var(--color-text-primary)',
                              marginBottom: '0.5rem'
                            }}>
                              Comentario *
                            </label>
                            <textarea
                              value={nuevoComentario.comentario}
                              onChange={(e) => setNuevoComentario({ ...nuevoComentario, comentario: e.target.value })}
                              rows={4}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--color-stroke)',
                                borderRadius: 'var(--radius-md)',
                                fontSize: 'var(--font-size-body)',
                                fontFamily: 'var(--font-ui)',
                                resize: 'vertical',
                                boxSizing: 'border-box'
                              }}
                              required
                            />
                          </div>
                          <button
                            type="submit"
                            disabled={submittingComentario}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: 'var(--color-cta-bg)',
                              color: 'var(--color-cta-text)',
                              border: 'none',
                              borderRadius: 'var(--radius-md)',
                              fontSize: 'var(--font-size-body)',
                              fontWeight: 'var(--font-weight-bold)',
                              cursor: submittingComentario ? 'not-allowed' : 'pointer',
                              opacity: submittingComentario ? 0.6 : 1
                            }}
                          >
                            {submittingComentario ? 'Enviando...' : 'Enviar Reseña'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {activeTab === 'galeria' && tienda.fotos && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '1rem',
                    width: '100%'
                  }}>
                    {tienda.fotos
                      .filter(foto => foto.activo)
                      .sort((a, b) => a.orden - b.orden)
                      .map((foto) => (
                        <div 
                          key={foto.id}
                          style={{
                            aspectRatio: '4/3',
                            borderRadius: 'var(--radius-lg)',
                            overflow: 'hidden',
                            border: '1px solid var(--color-stroke)',
                            background: 'var(--color-accent-gray)'
                          }}
                        >
                          <img
                            src={foto.imagen_url}
                            alt={foto.alt_text || `Imagen de ${tienda.nombre}`}
                            loading="lazy"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              transition: 'transform 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          />
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>

            {/* COLUMNA DERECHA: SIDEBAR (Contacto y Redes Sociales) */}
            {/* En mobile, esta columna aparece DEBAJO de la columna izquierda */}
            <div style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
              {/* TARJETA DE CONTACTO (¿Necesitas ayuda?) */}
              <div style={{
                background: 'var(--color-cta-bg)', // Fondo morado/azul del CTA - DEPENDE de: src/index.css
                color: 'white', // Texto blanco
                padding: window.innerWidth >= 768 ? '1.5rem' : '1rem', // Padding reducido en mobile
                borderRadius: 'var(--radius-lg)', // Bordes redondeados - DEPENDE de: src/index.css
                marginBottom: '1.5rem', // Espacio debajo
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}>
                <h3 style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-h3)',
                  fontWeight: 'var(--font-weight-bold)',
                  margin: '0 0 1rem 0'
                }}>
                  ¿Necesitas ayuda?
                </h3>
                <p style={{
                  fontSize: 'var(--font-size-body)',
                  lineHeight: 'var(--line-height-relaxed)',
                  margin: '0 0 1rem 0'
                }}>
                  Contacta directamente con {tienda.nombre}
                </p>
                <Link
                  to="/contacto"
                  style={{
                    display: 'block',
                    background: 'white',
                    color: 'var(--color-cta-bg)',
                    padding: '0.75rem 1.5rem',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-body)',
                    fontWeight: 'var(--font-weight-bold)',
                    textAlign: 'center',
                    textDecoration: 'none'
                  }}
                >
                  Contactar
                </Link>
              </div>

              {/* Social Links */}
              {(tienda.facebook || tienda.instagram || tienda.twitter) && (
                <div style={{
                  background: 'var(--color-accent-gray)',
                  padding: window.innerWidth >= 768 ? '1.5rem' : '1rem', // Padding reducido en mobile
                  borderRadius: 'var(--radius-lg)',
                  marginBottom: '1.5rem',
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                }}>
                  <h3 style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-h3)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--color-text-primary)',
                    margin: '0 0 1rem 0'
                  }}>
                    Síguenos
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {tienda.facebook && (
                      <a href={tienda.facebook} target="_blank" rel="noopener noreferrer" style={{
                        color: 'var(--color-text-primary)',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        📘 Facebook
                      </a>
                    )}
                    {tienda.instagram && (
                      <a href={tienda.instagram} target="_blank" rel="noopener noreferrer" style={{
                        color: 'var(--color-text-primary)',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        📷 Instagram
                      </a>
                    )}
                    {tienda.twitter && (
                      <a href={tienda.twitter} target="_blank" rel="noopener noreferrer" style={{
                        color: 'var(--color-text-primary)',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        🐦 Twitter
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* SECCIÓN: PREGUNTAS FRECUENTES (FAQs) */}
          {/* Solo se muestra si hay FAQs configuradas para esta tienda */}
          {faqs.length > 0 && (
            <section style={{
              marginTop: '3rem', // Espacio arriba (separación del contenido anterior)
              marginBottom: '2rem' // Espacio abajo
            }}>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--font-size-h2)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--color-text-primary)',
                marginBottom: '1.5rem'
              }}>
                Preguntas Frecuentes
              </h2>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                {faqs.map(faq => (
                  <details
                    key={faq.id}
                    style={{
                      background: 'var(--color-surface)',
                      border: '1px solid var(--color-stroke)',
                      borderRadius: 'var(--radius-md)',
                      padding: '1rem'
                    }}
                  >
                    <summary style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      fontWeight: 'var(--font-weight-bold)',
                      color: 'var(--color-text-primary)',
                      cursor: 'pointer',
                      listStyle: 'none'
                    }}>
                      {faq.pregunta}
                    </summary>
                    <p style={{
                      marginTop: '0.75rem',
                      fontSize: 'var(--font-size-body)',
                      color: 'var(--color-text-muted)',
                      lineHeight: 'var(--line-height-relaxed)'
                    }}>
                      {faq.respuesta}
                    </p>
                  </details>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  )
}
